import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Plus, MessageSquare, Trash2, Send, Bot, User, FileText, Database, Coins, Globe, Table, Braces, Code2, FileText as FileDoc, Paperclip, X, Bookmark, Pencil, Check } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Field, Input, Textarea } from '../../components/ui/Field';
import { Markdown } from '../../components/ui/Markdown';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { IndexStatusBadge } from '../../components/StatusBadges';
import { cn, formatUSD } from '../../lib/utils';
import { toast } from '../../store/uiStore';
import { useModelsStore } from '../../store/modelsStore';
import { agentsService } from '../../services/agentsService';
import { budgetService } from '../../services/budgetService';
import { agentFilesService } from '../../services/agentFilesService';
import { knowledgeBasesService } from '../../services/knowledgeBasesService';
import { usageService } from '../../services/usageService';
import { chatService, type Conversation, type ChatMessage, type ChatAttachment } from '../../services/chatService';
import { ArtifactModal } from './ArtifactViewer';
import { ApiError } from '../../services/apiClient';
import type { Agent, AgentFile, KnowledgeBase, Artifact } from '../../types';

// Chat de um agente (fixo). Vive dentro do editor do agente (aba "Chat").
export function AgentChat({ agentId }: { agentId: string }) {
  const labelOf = useModelsStore((s) => s.labelOf);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [toDelete, setToDelete] = useState<Conversation | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [viewingArtifact, setViewingArtifact] = useState<Artifact | null>(null);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [attToDelete, setAttToDelete] = useState<ChatAttachment | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [budgetBlocked, setBudgetBlocked] = useState(false);
  const [capSpent, setCapSpent] = useState<number | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [reqAmount, setReqAmount] = useState('5');
  const [reqReason, setReqReason] = useState('');
  const [requesting, setRequesting] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadConversations = useCallback(async (aid: string) => {
    setConversations(await chatService.listConversations(aid));
  }, []);

  const reloadAgent = useCallback(() => {
    agentsService.get(agentId).then((a) => setAgent(a ?? null));
  }, [agentId]);

  useEffect(() => {
    setActiveId(null);
    setMessages([]);
    setAttachments([]);
    setCapSpent(null);
    agentsService.get(agentId).then((a) => setAgent(a ?? null));
    knowledgeBasesService.list().then(setKbs).catch(() => {});
    loadConversations(agentId);
    // Gasto real do mês deste agente (para o indicador de teto).
    usageService.summary().then((s) => setCapSpent(s.byAgent[agentId]?.costUsd ?? 0)).catch(() => setCapSpent(null));
  }, [agentId, loadConversations]);

  const openConversation = useCallback(async (id: string) => {
    setActiveId(id);
    const { messages } = await chatService.get(id);
    setMessages(messages);
    chatService.listAttachments(id).then(setAttachments).catch(() => setAttachments([]));
  }, []);

  useEffect(() => {
    if (!activeId) return;
    const processing = attachments.some((a) => a.indexStatus === 'pending' || a.indexStatus === 'indexing');
    if (!processing) return;
    const t = setInterval(() => chatService.listAttachments(activeId).then(setAttachments).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, [attachments, activeId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  async function newConversation() {
    const conv = await chatService.create(agentId);
    setConversations((c) => [conv, ...c]);
    setActiveId(conv.id);
    setMessages([]);
    setAttachments([]);
  }

  function onPickFiles(list: FileList | null) {
    if (!list || !list.length) return;
    setPendingFiles(Array.from(list));
  }

  async function addToAgentFiles() {
    if (!pendingFiles || !agent) return;
    setAttaching(true);
    try {
      await agentFilesService.upload(agent.id, pendingFiles, agent.embeddingModel || 'text-embedding-3-small');
      toast.success('Arquivo(s) adicionado(s) aos Arquivos do agente. Indexando…');
      setPendingFiles(null);
      reloadAgent();
      setTimeout(reloadAgent, 3000);
      setTimeout(reloadAgent, 7000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao adicionar.');
    } finally {
      setAttaching(false);
    }
  }

  async function addToChatOnly() {
    if (!pendingFiles) return;
    setAttaching(true);
    try {
      let convId = activeId;
      if (!convId) {
        const conv = await chatService.create(agentId);
        setConversations((c) => [conv, ...c]);
        setActiveId(conv.id);
        convId = conv.id;
      }
      const updated = await chatService.uploadAttachments(convId, pendingFiles);
      setAttachments(updated);
      toast.success('Anexo(s) adicionado(s) a esta conversa. Indexando…');
      setPendingFiles(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao anexar.');
    } finally {
      setAttaching(false);
    }
  }

  async function confirmRemoveAttachment() {
    const att = attToDelete;
    if (!att || !activeId) return;
    setAttToDelete(null);
    setAttachments((a) => a.filter((x) => x.id !== att.id));
    try {
      await chatService.removeAttachment(activeId, att.id);
      toast.info('Anexo removido.');
    } catch {
      toast.error('Falha ao remover anexo.');
    }
  }

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    let convId = activeId;
    if (!convId) {
      try {
        const conv = await chatService.create(agentId);
        setConversations((c) => [conv, ...c]);
        setActiveId(conv.id);
        convId = conv.id;
      } catch {
        toast.error('Não foi possível iniciar a conversa.');
        setSending(false);
        return;
      }
    }
    const userMsg: ChatMessage = { id: `tmp_${Date.now()}`, role: 'user', content: text, createdAt: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    try {
      const res = await chatService.send(convId!, text);
      setMessages((m) => [...m, res.message]);
      setBudgetBlocked(false);
      if (res.cost && res.cost.monthlyCapUsd > 0) setCapSpent(res.cost.monthlySpentUsd);
      loadConversations(agentId);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Falha ao enviar.';
      if (err instanceof ApiError && err.code === 'budget_personal') setBudgetBlocked(true);
      setMessages((m) => [...m, { id: `err_${Date.now()}`, role: 'assistant', content: `⚠️ ${msg}`, createdAt: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  }

  async function submitRequest() {
    const amount = Number(reqAmount);
    if (!(amount > 0)) { toast.error('Informe um valor maior que zero.'); return; }
    setRequesting(true);
    try {
      await budgetService.requestGrant(amount, reqReason);
      toast.success('Pedido enviado ao admin da área.');
      setRequestOpen(false);
      setReqReason('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao solicitar.');
    } finally {
      setRequesting(false);
    }
  }

  function startRename(c: Conversation) {
    setRenamingId(c.id);
    setRenameValue(c.title);
  }

  async function commitRename() {
    const id = renamingId;
    const title = renameValue.trim();
    setRenamingId(null);
    if (!id || !title) return;
    setConversations((cs) => cs.map((c) => (c.id === id ? { ...c, title } : c)));
    try {
      await chatService.rename(id, title);
    } catch {
      toast.error('Falha ao renomear.');
      loadConversations(agentId);
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;
    await chatService.remove(toDelete.id);
    setConversations((c) => c.filter((x) => x.id !== toDelete.id));
    if (activeId === toDelete.id) {
      setActiveId(null);
      setMessages([]);
    }
    setToDelete(null);
  }

  const sessionCost = useMemo(() => messages.reduce((acc, m) => acc + (m.costUsd || 0), 0), [messages]);
  // Indicador de teto mensal do agente (gasto real x teto configurado).
  const cap = agent?.costControl?.monthlyCapBRL ?? 0;
  const capInfo = useMemo(() => {
    if (!(cap > 0) || capSpent == null) return null;
    const remaining = Math.max(0, cap - capSpent);
    const pct = Math.min(100, Math.round((capSpent / cap) * 100));
    const over = capSpent >= cap;
    const onExceed = agent?.costControl?.onExceed ?? 'warn';
    return { remaining, pct, over, onExceed };
  }, [cap, capSpent, agent?.costControl?.onExceed]);
  const readyFiles = (agent?.files || []).filter((f: AgentFile) => f.indexStatus === 'ready');
  const attachedKbs = useMemo(() => {
    if (!agent) return [];
    const ids = new Set(agent.knowledgeBaseIds ?? []);
    if (agent.knowledgeBaseId) ids.add(agent.knowledgeBaseId);
    return kbs.filter((k) => ids.has(k.id));
  }, [agent, kbs]);

  const thinkingPhrases = useMemo(() => {
    const p: string[] = [];
    if (agent?.guardrails?.moderationMode === 'model') p.push('Verificando políticas de conteúdo…');
    if (readyFiles.length || attachedKbs.length) {
      p.push('Buscando nas fontes…');
      p.push('Lendo os documentos…');
    }
    p.push('Pensando…', 'Formulando a resposta…', 'Quase lá…');
    return p;
  }, [agent, readyFiles.length, attachedKbs.length]);

  if (agent?.status === 'archived') {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-slate">
        Este agente está arquivado. Mude o status para "Ativo" ou "Rascunho" na aba Definição para conversar.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      {/* Sessões */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-line bg-white">
        <div className="border-b border-line p-3">
          <Button variant="primary" icon={Plus} className="w-full" onClick={newConversation}>Nova conversa</Button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-2">
          {conversations.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-slate">Nenhuma conversa ainda.</p>
          ) : (
            conversations.map((c) =>
              renamingId === c.id ? (
                <div key={c.id} className="mb-1 flex items-center gap-1 rounded-lg bg-cloud px-2 py-1.5">
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null); }}
                    onBlur={commitRename}
                    className="h-7 min-w-0 flex-1 rounded border border-line bg-white px-2 text-sm focus:border-brand-orange focus:outline-none"
                  />
                  <button onMouseDown={(e) => e.preventDefault()} onClick={commitRename} className="rounded p-1 text-slate hover:bg-white hover:text-brand-orange" aria-label="Salvar nome"><Check className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <div
                  key={c.id}
                  onClick={() => openConversation(c.id)}
                  className={cn('group mb-1 flex w-full cursor-pointer items-start gap-2 rounded-lg px-2.5 py-2 text-left transition-colors', activeId === c.id ? 'bg-brand-orange/10' : 'hover:bg-cloud')}
                >
                  <MessageSquare className={cn('mt-0.5 h-4 w-4 shrink-0', activeId === c.id ? 'text-brand-orange' : 'text-slate')} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{c.title}</span>
                    <span className="block text-[11px] text-slate">{c.messageCount ?? 0} msg · {formatUSD(c.costUsd || 0)}</span>
                  </span>
                  <span onClick={(e) => { e.stopPropagation(); startRename(c); }} className="hidden rounded p-1 text-slate hover:bg-white hover:text-ink group-hover:block" aria-label="Renomear">
                    <Pencil className="h-3.5 w-3.5" />
                  </span>
                  <span onClick={(e) => { e.stopPropagation(); setToDelete(c); }} className="hidden rounded p-1 text-slate hover:bg-danger/10 hover:text-danger group-hover:block" aria-label="Excluir">
                    <Trash2 className="h-3.5 w-3.5" />
                  </span>
                </div>
              )
            )
          )}
        </div>
      </aside>

      {/* Conversa */}
      <main className="flex min-w-0 flex-1 flex-col bg-cloud">
        <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
          <div className="mx-auto max-w-3xl space-y-4">
            {messages.length === 0 && !sending && (
              <div className="pt-10 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10"><MessageSquare className="h-6 w-6 text-brand-orange" /></div>
                <p className="text-sm font-medium text-ink">Converse com {agent?.name}</p>
                <p className="mt-1 text-xs text-slate">Usa a persona, os arquivos e as bases do agente. O histórico fica salvo. {agent && <>Modelo: {labelOf(agent.model)}.</>}</p>
              </div>
            )}
            {messages.map((m) => <Bubble key={m.id} msg={m} onOpenArtifact={setViewingArtifact} />)}
            {sending && <ThinkingBubble phrases={thinkingPhrases} />}
            <div ref={endRef} />
          </div>
        </div>

        <div className="border-t border-line bg-white px-5 py-3">
          {capInfo && (
            <div className="mx-auto mb-2 max-w-3xl">
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1 text-slate">
                  <Coins className="h-3 w-3" /> Teto mensal do agente
                </span>
                <span className={cn('font-medium', capInfo.over ? 'text-brand-orange' : 'text-slate')}>
                  {formatUSD(capSpent ?? 0)} / {formatUSD(cap)} · resta {formatUSD(capInfo.remaining)}
                </span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-cloud">
                <div
                  className={cn('h-full rounded-full transition-all', capInfo.over ? 'bg-brand-orange' : capInfo.pct >= 80 ? 'bg-amber-500' : 'bg-success')}
                  style={{ width: `${capInfo.pct}%` }}
                />
              </div>
              {capInfo.over && (
                <p className="mt-1 text-[10px] text-brand-orange">
                  Teto atingido — {capInfo.onExceed === 'block' ? 'novas mensagens serão bloqueadas.' : capInfo.onExceed === 'require_approval' ? 'novas mensagens exigem aprovação.' : 'apenas avisando; o agente continua respondendo.'}
                </p>
              )}
            </div>
          )}
          {budgetBlocked && (
            <div className="mx-auto mb-2 flex max-w-3xl items-center justify-between gap-3 rounded-xl border border-brand-orange/30 bg-brand-orange/5 px-3 py-2">
              <span className="flex items-center gap-1.5 text-xs text-ink"><Coins className="h-3.5 w-3.5 text-brand-orange" /> Seu saldo acabou.</span>
              <Button size="sm" variant="primary" onClick={() => setRequestOpen(true)}>Solicitar mais saldo</Button>
            </div>
          )}
          <div className="mx-auto flex max-w-3xl items-center gap-2">
            <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.csv,.txt,.md,.json" className="hidden" onChange={(e) => { onPickFiles(e.target.files); e.target.value = ''; }} />
            <Button variant="ghost" size="icon" icon={Paperclip} onClick={() => fileInputRef.current?.click()} aria-label="Anexar arquivo" title="Anexar arquivo" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={`Pergunte algo para ${agent?.name ?? 'o agente'}…`}
              className="h-11 flex-1 rounded-xl border border-line bg-white px-3.5 text-sm focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20"
            />
            <Button variant="primary" size="icon" icon={Send} onClick={send} loading={sending} aria-label="Enviar" />
          </div>
          {sessionCost > 0 && (
            <p className="mx-auto mt-1.5 flex max-w-3xl items-center gap-1 text-[11px] text-slate"><Coins className="h-3 w-3" /> custo desta conversa: {formatUSD(sessionCost)}</p>
          )}
        </div>
      </main>

      {/* Fontes */}
      <aside className="hidden w-64 shrink-0 flex-col border-l border-line bg-white xl:flex">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">Fontes</h2>
          <p className="mt-0.5 text-[11px] text-slate">Contexto do agente (RAG).</p>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-3">
          {attachedKbs.length > 0 && (
            <div className="mb-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate/70">Bases anexadas</p>
              <div className="space-y-1.5">
                {attachedKbs.map((kb) => (
                  <div key={kb.id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-2">
                    <Database className="h-4 w-4 shrink-0 text-navy" />
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{kb.name}</span>
                    <span className="text-[10px] text-slate">{kb.documentCount}d</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {attachments.length > 0 && (
            <div className="mb-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate/70">Anexos desta conversa</p>
              <div className="space-y-1.5">
                {attachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-2">
                    <Paperclip className="h-4 w-4 shrink-0 text-brand-orange" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{a.name}</span>
                      <span className="text-[10px] text-slate">{a.indexStatus === 'ready' ? `${a.chunkCount ?? 0} chunks` : a.indexStatus === 'error' ? (a.error || 'erro') : 'indexando…'}</span>
                    </span>
                    {(a.indexStatus === 'pending' || a.indexStatus === 'indexing') && <IndexStatusBadge status={a.indexStatus} />}
                    <button onClick={() => setAttToDelete(a)} className="rounded p-1 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Remover anexo"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate/70">Arquivos do agente</p>
          {readyFiles.length === 0 ? (
            <p className="rounded-lg bg-cloud px-2.5 py-3 text-xs text-slate">Nenhum arquivo indexado (aba Arquivos).</p>
          ) : (
            <div className="space-y-1.5">
              {readyFiles.map((f) => (
                <div key={f.id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-2">
                  <FileText className="h-4 w-4 shrink-0 text-slate" />
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{f.name}</span>
                  <span className="text-[10px] text-slate">{f.chunkCount ?? 0}c</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={confirmDelete} title="Excluir conversa" message={`Excluir "${toDelete?.title}"? O histórico será removido.`} />
      <ConfirmDialog open={!!attToDelete} onClose={() => setAttToDelete(null)} onConfirm={confirmRemoveAttachment} title="Remover anexo" message={`Remover "${attToDelete?.name}" desta conversa? Os trechos indexados deste anexo também serão removidos.`} />
      <ArtifactModal artifact={viewingArtifact} open={!!viewingArtifact} onClose={() => setViewingArtifact(null)} />

      <Modal
        open={requestOpen}
        onClose={() => !requesting && setRequestOpen(false)}
        title="Solicitar mais saldo"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRequestOpen(false)}>Cancelar</Button>
            <Button variant="primary" loading={requesting} onClick={submitRequest}>Enviar pedido</Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate">O pedido vai para o admin da sua área aprovar; o valor é debitado do budget da área.</p>
        <Field label="Valor extra (US$)">
          <Input type="number" min={1} step="1" value={reqAmount} onChange={(e) => setReqAmount(e.target.value)} />
        </Field>
        <Field label="Justificativa (opcional)" className="mb-0">
          <Textarea rows={2} value={reqReason} onChange={(e) => setReqReason(e.target.value)} placeholder="Para que você precisa do saldo extra?" />
        </Field>
      </Modal>

      <Modal open={!!pendingFiles} onClose={() => !attaching && setPendingFiles(null)} title="Adicionar arquivo">
        <p className="mb-1 text-sm text-slate">{pendingFiles?.length === 1 ? pendingFiles[0].name : `${pendingFiles?.length ?? 0} arquivo(s)`} — onde usar?</p>
        <div className="mt-4 space-y-2.5">
          <button onClick={addToAgentFiles} disabled={attaching} className="flex w-full items-start gap-3 rounded-xl border border-line p-3 text-left transition-colors hover:border-brand-orange hover:bg-brand-orange/5 disabled:opacity-50">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy/10 text-navy"><Bookmark className="h-5 w-5" /></span>
            <span>
              <span className="block text-sm font-semibold text-ink">Adicionar aos Arquivos do agente</span>
              <span className="block text-xs text-slate">Permanente: indexado e disponível em todas as conversas deste agente.</span>
            </span>
          </button>
          <button onClick={addToChatOnly} disabled={attaching} className="flex w-full items-start gap-3 rounded-xl border border-line p-3 text-left transition-colors hover:border-brand-orange hover:bg-brand-orange/5 disabled:opacity-50">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-orange/10 text-brand-orange"><Paperclip className="h-5 w-5" /></span>
            <span>
              <span className="block text-sm font-semibold text-ink">Usar só nesta conversa</span>
              <span className="block text-xs text-slate">Temporário: vale só para este chat e some quando a conversa for excluída.</span>
            </span>
          </button>
        </div>
      </Modal>
    </div>
  );
}

const ARTIFACT_ICON: Record<Artifact['type'], typeof Globe> = {
  html: Globe, document: FileDoc, table: Table, json: Braces, code: Code2, image: FileDoc,
};

function ThinkingBubble({ phrases }: { phrases: string[] }) {
  const list = phrases.length ? phrases : ['Pensando…'];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % list.length), 1800);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);
  return (
    <div className="flex gap-2.5">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-orange text-white"><Bot className="h-3.5 w-3.5" /></div>
      <div className="flex items-center gap-2 rounded-2xl bg-white px-3.5 py-2.5 shadow-sm">
        <span className="flex gap-1">
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-orange/70 [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-orange/70 [animation-delay:150ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-orange/70 [animation-delay:300ms]" />
        </span>
        <span className="text-xs text-slate">{list[i]}</span>
      </div>
    </div>
  );
}

function Bubble({ msg, onOpenArtifact }: { msg: ChatMessage; onOpenArtifact: (a: Artifact) => void }) {
  const isUser = msg.role === 'user';
  return (
    <div className={cn('flex gap-2.5', isUser && 'flex-row-reverse')}>
      <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white', isUser ? 'bg-navy' : 'bg-brand-orange')}>
        {isUser ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
      </div>
      <div className={cn('min-w-0 max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm', isUser ? 'whitespace-pre-wrap bg-navy text-white' : 'bg-white text-ink shadow-sm')}>
        {isUser ? msg.content : <Markdown>{msg.content}</Markdown>}
        {!isUser && msg.artifacts && msg.artifacts.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {msg.artifacts.map((art) => {
              const Icon = ARTIFACT_ICON[art.type] ?? FileDoc;
              return (
                <button key={art.id} onClick={() => onOpenArtifact(art)} className="flex w-full items-center gap-2.5 rounded-xl border border-line bg-cloud px-3 py-2 text-left transition-colors hover:border-brand-orange hover:bg-brand-orange/5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-orange/10 text-brand-orange"><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{art.name}</span>
                    <span className="block text-[11px] text-slate">{art.type} · abrir / exportar</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {!isUser && msg.sources && msg.sources.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1 border-t border-line pt-2">
            {Array.from(new Set(msg.sources.map((s) => (s.kb ? `${s.kb} · ${s.fileName}` : s.fileName)))).map((s) => (
              <span key={s} className="inline-flex items-center gap-1 rounded-md bg-cloud px-1.5 py-0.5 text-[10px] text-slate"><FileText className="h-2.5 w-2.5" /> {s}</span>
            ))}
          </div>
        )}
        {!isUser && msg.costUsd != null && msg.costUsd > 0 && (
          <div className="mt-1 text-right text-[10px] text-slate/70">{formatUSD(msg.costUsd)}</div>
        )}
      </div>
    </div>
  );
}
